import {DI_TOKENS} from '@/di/di.tokens';
import {SettingsType} from '@/stores/SettingsStore/SettingsStore.constants';

import {injected} from 'brandi';
import {Howl} from 'howler';

import {WindowVisibilityService} from '@/services/WindowVisibilityService/WindowVisibilityService';
import {SettingsStore} from '@/stores/SettingsStore/SettingsStore';
import {Config} from '@/types';

export enum ClickerSoundsBundle {
  Tap = 'Tap',
  TapComet = 'TapComet',
  KotletkaIdle = 'KotletkaIdle',
  KotletkaTapComet = 'KotletkaTapComet',
  KotletkaTapSad = 'KotletkaTapSad',
  KotletkaTapHappy = 'KotletkaTapHappy',
  OpenModal = 'OpenModal',
  CloseModal = 'CloseModal',
  BuyImprovement = 'BuyImprovement',
  GetReward = 'GetReward',
  RewardWrongTap = 'RewardWrongTap',
  DailyGameWin = 'DailyGameWin',
  EnergyRecharge = 'EnergyRecharge',
  BackgroundMusic = 'BackgroundMusic',
}

export type ClickSounds =
  | ClickerSoundsBundle.Tap
  | ClickerSoundsBundle.TapComet
  | ClickerSoundsBundle.OpenModal
  | ClickerSoundsBundle.CloseModal
  | ClickerSoundsBundle.EnergyRecharge
  | ClickerSoundsBundle.GetReward
  | ClickerSoundsBundle.RewardWrongTap
  | ClickerSoundsBundle.BuyImprovement
  | ClickerSoundsBundle.KotletkaTapComet
  | ClickerSoundsBundle.KotletkaTapHappy
  | ClickerSoundsBundle.KotletkaTapSad
  | ClickerSoundsBundle.DailyGameWin;

export type ExtraSounds = ClickerSoundsBundle.KotletkaIdle | ClickerSoundsBundle.KotletkaTapComet;

export class ClickerAudioPlayer {
  private audioSourceMap: Record<ClickerSoundsBundle, string> = {
    [ClickerSoundsBundle.Tap]: this.getFullPath('tap-2024-10-14-12-09-09.mp3'),
    [ClickerSoundsBundle.TapComet]: this.getFullPath('tap_comet-2024-10-14-12-10-56.mp3'),
    [ClickerSoundsBundle.KotletkaIdle]: this.getFullPath('kotletka_idle-2024-10-14-12-12-12.mp3'),
    [ClickerSoundsBundle.KotletkaTapSad]: this.getFullPath('kotletka_sad-2024-10-14-12-13-33.mp3'),
    [ClickerSoundsBundle.KotletkaTapHappy]: this.getFullPath('kotletka_happy-2024-10-14-12-14-20.mp3'),
    [ClickerSoundsBundle.KotletkaTapComet]: this.getFullPath('kotletka_tap_comet-2024-10-15-08-47-27.mp3'),
    [ClickerSoundsBundle.OpenModal]: this.getFullPath('open_page_modal-2024-10-14-12-16-06.mp3'),
    [ClickerSoundsBundle.CloseModal]: this.getFullPath('close_page_modal-2024-10-14-12-17-13.mp3'),
    [ClickerSoundsBundle.BuyImprovement]: this.getFullPath('improvement_or_reward-2024-10-14-15-38-00.mp3'),
    [ClickerSoundsBundle.GetReward]: this.getFullPath('improvement_or_reward-2024-10-14-15-38-00.mp3'),
    [ClickerSoundsBundle.EnergyRecharge]: this.getFullPath('energy_recharge-2024-10-14-12-18-59.mp3'),
    [ClickerSoundsBundle.BackgroundMusic]: this.getFullPath('background-2024-10-14-12-07-13.mp3'),
    [ClickerSoundsBundle.RewardWrongTap]: this.getFullPath('reward_wrong_tap-2024-10-15-07-33-16.mp3'),
    [ClickerSoundsBundle.DailyGameWin]: this.getFullPath('daily_game_win-2024-11-01-09-34-21.mp3'),
  };
  private readonly audioMap: Record<ClickerSoundsBundle, Howl>;

  // клик
  private playingClick: Howl | null;
  // параллельные дополнительные звуки
  private playingExtraSound: Howl | null;

  constructor(
    private readonly config: Config,
    private readonly windowVisibilityService: WindowVisibilityService,
    private readonly settingsStore: SettingsStore,
  ) {
    Howler.autoSuspend = false;

    this.audioMap = this.getAudioConfig();

    this.playingClick = null;
    this.playingExtraSound = null;
  }

  public playClickSound(name: ClickSounds): void {
    this.playingClick = this.audioMap[name];
    this.playingClick?.play();
  }

  public playExtraSound(name: ExtraSounds): void {
    if (this.playingExtraSound?.playing()) return;

    this.playingExtraSound = this.audioMap[name];
    this.playingExtraSound?.play();
  }

  private playMainBackgroundMusic = (): void => {
    this.audioMap[ClickerSoundsBundle.BackgroundMusic].play();
  };

  private pauseMainBackgroundMusic = (): void => {
    this.audioMap[ClickerSoundsBundle.BackgroundMusic].pause();
  };

  public startPlayAudio(): void {
    this.windowVisibilityService.onVisibilityChange(this.checkFocusedOrDocumentVisible);
  }

  public stopPlayAudio(): void {
    Object.values(this.audioMap).forEach((sound) => {
      sound.stop();
    });
    this.windowVisibilityService.offVisibilityChange(this.checkFocusedOrDocumentVisible);
  }

  public checkSoundsMute = (): void => {
    Object.entries(this.audioMap).forEach(([key, sound]) => {
      if (key !== ClickerSoundsBundle.BackgroundMusic) {
        sound.stop();
        sound.mute(this.isSoundsMuted());
      }
    });
  };

  public checkMusicMute = (): void => {
    if (this.isMusicMuted()) {
      this.audioMap[ClickerSoundsBundle.BackgroundMusic].mute(true);
    } else {
      this.audioMap[ClickerSoundsBundle.BackgroundMusic].mute(false);
    }
  };

  private checkFocusedOrDocumentVisible = (isVisible: boolean): void => {
    if (isVisible) {
      this.playMainBackgroundMusic();
    } else {
      this.pauseMainBackgroundMusic();
    }
  };

  private getAudioConfig(): Record<ClickerSoundsBundle, Howl> {
    return Object.keys(this.audioSourceMap).reduce<Record<ClickerSoundsBundle, Howl>>((config, key) => {
      const bundleKey = key as ClickerSoundsBundle;
      let audio: Howl;

      if (bundleKey === ClickerSoundsBundle.BackgroundMusic) {
        audio = new Howl({
          src: [this.audioSourceMap[bundleKey]],
          volume: 0.2,
          loop: true,
        });
      } else {
        audio = new Howl({
          src: [this.audioSourceMap[bundleKey]],
        });

        if (bundleKey === ClickerSoundsBundle.OpenModal || bundleKey === ClickerSoundsBundle.CloseModal) {
          audio.volume(0.7);
        }
      }

      config[bundleKey] = audio;

      return config;
    }, {} as Record<ClickerSoundsBundle, Howl>);
  }

  private getFullPath(relativePath: string): string {
    return `${this.config.cdnUrl}/static/invest24/${relativePath}`;
  }

  private isMusicMuted = (): boolean => {
    return !this.settingsStore.getSettingValue(SettingsType.Music);
  };

  private isSoundsMuted = (): boolean => {
    return !this.settingsStore.getSettingValue(SettingsType.Sounds);
  };
}

injected(ClickerAudioPlayer, DI_TOKENS.config, DI_TOKENS.windowVisibilityService, DI_TOKENS.settingsStore);
